import { useRouter } from "next/router";
import React, { FC, createContext, useContext, useState, ReactNode } from "react";
import { useMount } from "react-use";

type HistoryItem = { href: string; as?: string };

type History = {
  histories: HistoryItem[];
  push(args: HistoryItem): void; // 新しい履歴を追加
  replace(args: HistoryItem): void; // 最新の履歴を上書き
  pop(): { popped: HistoryItem | null; nextTop: HistoryItem | null }; // 最新の履歴を削除
};

const context = createContext<History>({
  histories: [],
  push: () => {},
  replace: () => {},
  pop: () => ({ popped: null, nextTop: null }),
});
const { Provider } = context;

export const useHistory = () => useContext(context);

export const usePushHistory = () => {
  const router = useRouter();
  const history = useHistory();

  useMount(() => {
    history.push({
      href: router.pathname,
      as: router.asPath,
    });
  });

  return history;
};

export const HistoryProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const router = useRouter();

  const [historyItems, setHistoryItems] = useState<{ href: string; as?: string }[]>([
    {
      href: router.pathname,
      as: router.asPath,
    },
  ]);

  const push = ({ href, as }: HistoryItem) => {
    setHistoryItems((prevHistoryItems) => {
      // 直接アクセスされた場合にhistoryが重複しないように
      if (prevHistoryItems.length > 0 && prevHistoryItems[0]?.href === href) {
        return prevHistoryItems;
      }

      return [{ href, as }, ...prevHistoryItems];
    });
  };

  const replace = ({ href, as }: HistoryItem) => {
    setHistoryItems((prevHistoryItems) => [{ href, as }, ...prevHistoryItems.slice(1)]);
  };

  const pop = () => {
    if (historyItems.length === 0) {
      return { popped: null, nextTop: null };
    }

    const popped = historyItems[0] ?? null;
    const nextTop = historyItems[1] ?? null;

    setHistoryItems((historyItems) => historyItems.slice(1));

    return {
      popped,
      nextTop,
    };
  };

  return <Provider value={{ histories: historyItems, push, replace, pop }}>{children}</Provider>;
};
